import { Box, Stack, StackProps } from "@mui/material";
import { EnumRankingType, IRankingData } from "src/components/common/ranking/types";
import { Typography, TypographyProps } from "src/components-mui/base/typography/typography";
import { PopoverButtonIconInfo } from "src/components-mui/base/wf-popover/popover-button-icon-info";
import { WfPopover } from "src/components-mui/base/wf-popover/wf-popover";
import { RankingValue } from "src/components-mui/common/ranking/ranking-value";
import { useDateTimeFormatter } from "src/hooks/formatters/use-date-time-formatter";
import { ITooltip } from "src/types/common";
import { toSxArray } from "src/utils/mui/sx/sx";

const KEY_FIGURE_TEST_ID = "key-figure";

interface IKeyFigureProps extends Omit<StackProps, "fontSize"> {
    ranking: IRankingData;
    value?: string;
    tooltip?: ITooltip | null;
    /* On true removes `- place` in EnumRankingType.RankingPlace */
    hidePoints?: boolean;
    rankingValueFontWeight?: Required<TypographyProps>["fontWeight"];
    fontSize?: NonNullable<TypographyProps["fontSize"]>;
    height?: number | string;
}

export const KeyFigure = ({
    ranking,
    value,
    tooltip,
    hidePoints,
    rankingValueFontWeight,
    height = 56,
    fontSize,
    sx,
    ...stackProps
}: IKeyFigureProps) => {
    const { formatDateShort } = useDateTimeFormatter();

    return (
        <Stack
            direction="row"
            data-test-id={KEY_FIGURE_TEST_ID}
            {...stackProps}
            sx={[{ justifyContent: "space-between", alignItems: "center", width: "100%", height, fontSize }, ...toSxArray(sx)]}
        >
            <Stack component="dt" direction="row" sx={{ alignItems: "center", m: 0 }}>
                <Stack direction="column" sx={{ alignItems: "center" }}>
                    <Typography sx={{ fontSize, lineHeight: "inherit" }}>{ranking.label}</Typography>
                    {ranking.type === EnumRankingType.Performance && ranking.creationDate && (
                        <Typography
                            component="time"
                            dateTime={new Date(ranking.creationDate).toISOString()}
                            noWrap
                            sx={{ fontSize: "xs", wordBreak: "break-all", alignSelf: "flex-start", color: "gray.500" }}
                        >
                            {formatDateShort(ranking.creationDate)}
                        </Typography>
                    )}
                </Stack>
                {tooltip && (
                    <WfPopover
                        PopoverTrigger={<PopoverButtonIconInfo ariaLabel={ranking.label} sx={{ ml: 1 }} />}
                        {...tooltip}
                        slotProps={{ popper: { zIndex: 1 } }}
                    />
                )}
            </Stack>
            <Box component="dd" sx={{ m: 0, lineHeight: 1.5 }}>
                <RankingValue
                    fontWeight={rankingValueFontWeight ?? "semibold"}
                    size={fontSize}
                    data={ranking}
                    hidePoints={hidePoints}
                    value={value}
                />
            </Box>
        </Stack>
    );
};
